import { ClusterStep, IHierarchData, IHierarchyParameters, ITreeNode, RenderedNode } from '../hierarchy-interfaces';
import { DistanceType, LinkageType } from '../../cluster-interfaces';
import { euclideanDistance, manhattanDistance, round } from '../../cluster-provider';

export const DENDROGRAM_X_PADDING = 100;
export const DENDROGRAM_Y_MIN_PADDING = 60;
export const DENDROGRAM_DOT_RADIUS = 5;
export const DENDROGRAM_VERTICAL_LINE_HEIGHT = 0; // 30

export const agglomerativeClustering = (
    params: IHierarchyParameters,
    linkage: LinkageType,
    distance: DistanceType,
    isSimilarityMatrix: boolean,
): IHierarchData => {
    let table = params.table;
    let rowNames = params.rowNames;
    const points = params.points;

    let rootId = '';

    if(isSimilarityMatrix && table) {
        const _table : number[][] = [];

        for(const item of table) {
            _table.push(item.map(cell => round(1 - cell)));
        }

        table = _table;
    }

    if (!table && points) {
        rowNames = points.map(p => p[0]);

        const getDistanceFn = distance === DistanceType.Manhattan
            ? manhattanDistance
            : euclideanDistance;

        table = points.map(([_, ...coordA]) =>
            points.map(([__, ...coordB]) =>
                getDistanceFn(coordA, coordB)
            )
        );
    }

    if (!table || !rowNames || table.length !== rowNames.length) {
        throw new Error("Invalid input: table and rowNames must be provided and of equal length.");
    }

    // -------
    const map: Map<string, ITreeNode> = new Map();

    for(const letter of rowNames) {
        map.set(letter, {
            letter,
            height: 0,
        });
    }
    // -------

    const n = rowNames.length;
    const clusters: string[][] = rowNames.map(name => [name]);
    const active = new Set<number>(Array.from({ length: n }, (_, i) => i));
    const steps: ClusterStep[] = [];
    const calculations: string[] = [];

    const getDistance = (a: number[], b: number[]): number => {

        if(linkage === LinkageType.SingleLinkage) {
            let min = Infinity;
            for (const i of a) {
                for (const j of b) {
                    const d = i === j ? Infinity : table[i][j];
                    if (d < min) min = d;
                }
            }
            return min;
        }
        else{
            let max = -Infinity;
            for (const i of a) {
                for (const j of b) {
                    if (i === j) continue;
                    const d = table[i][j];
                    if (d > max) max = d;
                }
            }
            return max;
        }
    };

    while (active.size > 1) {
        let minDist = Infinity;
        let mergeA = -1;
        let mergeB = -1;

        const indices = Array.from(active);

        const stepComparisons: string[] = [];

        for (let i = 0; i < indices.length; i++) {
            for (let j = i + 1; j < indices.length; j++) {
                const idxA = indices[i];
                const idxB = indices[j];
                const clusterA = clusters[idxA];
                const clusterB = clusters[idxB];
                const aIndices = clusterA.map(name => rowNames.indexOf(name));
                const bIndices = clusterB.map(name => rowNames.indexOf(name));

                const dist = getDistance(aIndices, bIndices);
                stepComparisons.push(`[${clusterA.join(',')}]-[${clusterB.join(',')}] = ${dist.toFixed(3)}`);

                if (dist < minDist) {
                    minDist = dist;
                    mergeA = idxA;
                    mergeB = idxB;
                }
            }
        }

        if (mergeA === -1 || mergeB === -1) break;

        // Build tree node: start ---------------------------
        const leftLetter = clusters[mergeA].join(',');
        const rightLetter = clusters[mergeB].join(',')
        const combinedLetter = leftLetter + ',' + rightLetter;
        rootId = combinedLetter;

        const combined: ITreeNode = {
            letter: combinedLetter,
            left: map.get(leftLetter),
            right: map.get(rightLetter),
            height: minDist,
        };

        map.set(combinedLetter, combined);
        // Build tree node: end ---------------------------

        // Merge clusters --------------------
        const selectedA = clusters[mergeA].slice();
        const selectedB = clusters[mergeB].slice();

        const mergedCluster = [...clusters[mergeA], ...clusters[mergeB]];
        clusters[mergeA] = mergedCluster;
        clusters[mergeB] = [];

        active.delete(mergeB); // Remove the merged-out cluster

        steps.push({
            clusters: mergedCluster.slice().sort(),
            distance: minDist
        });

        calculations.push(`Step ${steps.length} (${linkage === LinkageType.SingleLinkage ? 'Single' : 'Complete'} Linkage)`);
        calculations.push(...stepComparisons);
        calculations.push(`→ Chosen: [${selectedA.join(',')}]-[${selectedB.join(',')}] => ${minDist}`);
    }

    return {
        steps,
        map,
        rootId,
        calculations,
    };
};

export const buildTree = (map: Map<string, ITreeNode>, letters: string[]): {
    nodes: RenderedNode[],
    width: number,
    height: number,
} => {

    const width = letters.length * DENDROGRAM_X_PADDING;

    // Calculate height: start ----------------------
    const internalNodesCount = map.size - letters.length;
    const a = DENDROGRAM_Y_MIN_PADDING;
    const d = a / 4;
    const n = internalNodesCount;

    const mergeLevelsHeight = n > 0
        ? (n / 2) * (2 * a + (n - 1) * d)
        : 0;

    const height = DENDROGRAM_Y_MIN_PADDING + mergeLevelsHeight + DENDROGRAM_VERTICAL_LINE_HEIGHT;

    /*let height = DENDROGRAM_Y_MIN_PADDING;
    const countWithoutLetters = map.size - letters.length - 1;
    for(let k=0; k<countWithoutLetters; k++) {
        height += DENDROGRAM_Y_MIN_PADDING * k;
    }
    height += DENDROGRAM_Y_MIN_PADDING;*/

    // Calculate height: end ----------------------

    const nodes: RenderedNode[] = [];

    const nodesMap: Map<String, RenderedNode> = new Map();

    // Single letters ---------------------------------
    for(let i=0; i<letters.length; i++) {
        const letter = letters[i];
        const x = DENDROGRAM_X_PADDING * i + DENDROGRAM_X_PADDING/2;
        const y = height;

        const node: RenderedNode = {
            letter,
            x,
            y,
            lines: [],
        };

        nodes.push(node);
        nodesMap.set(node.letter, node);
    }

    // Handle other items ----------------------------
    let nextHeightSize  = DENDROGRAM_Y_MIN_PADDING;
    for(const [_letter, item] of map) {

        // skip single letters -----
        if(item.height === 0) continue;

        // should be both left and right nodes
        if(!item.left || !item.right) continue;

        const left = nodesMap.get(item.left.letter);
        const right = nodesMap.get(item.right.letter);
        if(!left || !right) continue;

        const x = (left.x + right.x) / 2;
        const y = Math.min(left.y, right.y) - nextHeightSize;

        const node: RenderedNode = {
            letter: item.letter,
            x,
            y,
            lines: [
                {
                    x1: left.x,
                    y1: left.y,
                    x2: left.x,
                    y2: y + DENDROGRAM_VERTICAL_LINE_HEIGHT,
                },
                {
                    x1: right.x,
                    y1: right.y,
                    x2: right.x,
                    y2: y + DENDROGRAM_VERTICAL_LINE_HEIGHT,
                },
                {
                    x1: x,
                    y1: y,
                    x2: x,
                    y2: y + DENDROGRAM_VERTICAL_LINE_HEIGHT,
                    stroke: 'blue'
                },
                {
                    x1: left.x,
                    y1: y + DENDROGRAM_VERTICAL_LINE_HEIGHT,
                    x2: right.x,
                    y2: y + DENDROGRAM_VERTICAL_LINE_HEIGHT,
                    stroke: 'blue'
                }
            ]
        };

        nodes.push(node);
        nodesMap.set(node.letter, node);
        nextHeightSize += DENDROGRAM_Y_MIN_PADDING/4;
    }

    return {
        nodes,
        width,
        height,
    };
};
